import React, {useEffect, useMemo, useState} from 'react';
import {useHistory} from "react-router-dom";
import {useSelector} from "react-redux";
import {CAlert, CButton, CInput, CSelect} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import AsyncSelect from "react-select/async";
import {getApi} from "../../apis/backendApis";
import useLoading from "../../services/useLoading";
import useAlert from "../../services/useAlert";
import {States} from "../../store/ducks/rootReducer";
import {TIPO_PAPEL_CLIENTE, dataBr, erroDaApi} from "./modelo";
import {opcoesDePessoas, textoSetores, usePessoasPapel} from "./pessoasPapel";
import '../../scss/styles-processo.scss';

interface FluxoAtivo {
    id: number,
    codigo: string,
    nome: string,
    versao: number,
    itens_obrigatorios: number,
    modulos_opcionais: Array<{codigo: string, nome: string, itens: number}>,
    papeis: Array<{codigo: string, nome: string, tipo: number, usuario: {id: number, nome: string} | null}>,
}

interface ProcessoExistente {
    id: number,
    fluxo_id: number,
    titulo: string,
    situacao: number,
    situacao_nome: string,
    data_inicio: string,
}

interface Pessoa {
    usuario_id?: number | '',
    contato_nome?: string,
    contato_email?: string,
    contato_telefone?: string,
    contato_cargo?: string,
}

interface Previa {
    inicio: string,
    fim: string,
    dias_uteis: number,
    itens: number,
    etapas: Array<{nome: string, inicio: string, fim: string, itens: number}>,
    papeis_usados: Array<{codigo: string, itens: number}>,
    etapas_sem_itens: string[],
}

const PASSOS = ['Cliente e fluxo', 'Módulos contratados', 'Responsáveis', 'Confirmar'];

const hoje = () => {
    const d = new Date();
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
};

/**
 * Assistente de abertura do processo (#480058, 03-regras-negocio.md item 2):
 * cliente e fluxo -> modulos contratados -> quem e responsavel por cada papel
 * -> previa do cronograma e confirmacao.
 */
const ProcessoNovo = () => {
    const history = useHistory();
    const [setLoading] = useLoading();
    const [showAlert] = useAlert();
    const usuarioAtual = useSelector((state: States) => state.login.data?.user);

    const [passo, setPasso] = useState(0);
    const [fluxos, setFluxos] = useState<FluxoAtivo[]>([]);
    const [usuarios, setUsuarios] = useState<Array<{id: number, nome: string}>>([]);
    const [erro, setErro] = useState<string | null>(null);
    const [previa, setPrevia] = useState<Previa | null>(null);

    const [fluxoId, setFluxoId] = useState<number | ''>('');
    const [cliente, setCliente] = useState<{value: number, label: string} | null>(null);
    const [dataInicio, setDataInicio] = useState(hoje());
    const [gestorId, setGestorId] = useState<number | ''>(usuarioAtual?.id ?? '');
    const [complexidade, setComplexidade] = useState<number | ''>('');
    const [titulo, setTitulo] = useState('');
    const [modulos, setModulos] = useState<string[]>([]);
    // Webhook do Mattermost que recebe os avisos do processo ('' = nao avisa).
    const [webhooks, setWebhooks] = useState<Array<{id: number, nome: string}>>([]);
    const [webhookId, setWebhookId] = useState<number | ''>('');
    const [pessoas, setPessoas] = useState<{[codigo: string]: Pessoa}>({});
    // Processos que o cliente ja tem (menos cancelados): repetir o fluxo pede confirmacao.
    const [existentes, setExistentes] = useState<ProcessoExistente[]>([]);
    const [confirmaRepetido, setConfirmaRepetido] = useState(false);

    useEffect(() => {
        const api = getApi();
        api.get('processo/fluxos-ativos').then(resposta => {
            setFluxos(resposta.data);
            if (resposta.data.length === 1) {
                setFluxoId(resposta.data[0].id);
            }
        });
        api.get('processo/webhooks-mattermost').then(resposta => {
            setWebhooks(resposta.data.webhooks || []);
            if (resposta.data.padrao && (resposta.data.webhooks || []).some((w: any) => w.id === resposta.data.padrao)) {
                setWebhookId(resposta.data.padrao);
            }
        }).catch(() => setWebhooks([]));
        api.get('/usuarios/suggest').then(resposta => {
            setUsuarios((resposta.data.usuarios || []).map((par: any) => {
                const [id, nome] = Object.entries(par)[0];
                return {id: Number(id), nome: String(nome)};
            }));
        });
    }, []);

    const fluxo = fluxos.find(f => f.id === fluxoId);
    // Quem pode ocupar os papeis: os usuarios dos setores do fluxo (#484909).
    const pessoasPapel = usePessoasPapel({fluxo_id: fluxoId || undefined});
    const opcoesPapel = opcoesDePessoas(pessoasPapel, usuarios);

    // Chegou a lista do fluxo: tira dos papeis a pessoa padrao que esta fora dos setores.
    useEffect(() => {
        if (!pessoasPapel?.restrito) return;
        const permitidos = new Set(pessoasPapel.usuarios.map(u => u.id));
        setPessoas(atual => {
            const novo = {...atual};
            Object.keys(novo).forEach(codigo => {
                const id = novo[codigo].usuario_id;
                if (id && !permitidos.has(id)) novo[codigo] = {...novo[codigo], usuario_id: ''};
            });
            return novo;
        });
    }, [pessoasPapel]);

    // Pessoa padrao de cada papel: a do cadastro do fluxo; o GP e o gestor.
    useEffect(() => {
        if (!fluxo) {
            return;
        }
        const padrao: {[codigo: string]: Pessoa} = {};
        fluxo.papeis.forEach(papel => {
            padrao[papel.codigo] = papel.tipo === TIPO_PAPEL_CLIENTE ? {} : {usuario_id: papel.usuario?.id ?? ''};
        });
        if (padrao.GP && gestorId) {
            padrao.GP = {usuario_id: gestorId};
        }
        setPessoas(padrao);
        // Todos os modulos ja vem marcados; desmarca-se o que o cliente nao contratou.
        setModulos(fluxo.modulos_opcionais.map(m => m.codigo));
    }, [fluxoId, fluxos.length]);

    useEffect(() => {
        setExistentes([]);
        if (!cliente) {
            return;
        }
        let vale = true;
        getApi().get(`processo/abertura/existentes/${cliente.value}`).then(resposta => {
            if (vale) {
                setExistentes(resposta.data);
            }
        });
        return () => { vale = false; };
    }, [cliente]);

    useEffect(() => setConfirmaRepetido(false), [cliente, fluxoId]);

    const repetidos = existentes.filter(p => p.fluxo_id === fluxoId);
    const fluxosDoCliente = useMemo(() => new Set(existentes.map(p => p.fluxo_id)), [existentes]);

    // Inativos nao recebem processo: a busca da abertura ja os deixa de fora.
    const buscaClientes = (texto: string) => getApi().get('processo/abertura/clientes', {params: {busca: texto}}).then(resposta => resposta.data);

    const corpo = () => ({
        fluxo_id: fluxoId,
        cliente_id: cliente?.value,
        data_inicio: dataInicio,
        gestor_id: gestorId || undefined,
        complexidade: complexidade || undefined,
        titulo: titulo.trim() || undefined,
        webhook_mattermost_id: webhookId || null,
        confirma_repetido: confirmaRepetido || undefined,
        modulos,
        papeis: Object.entries(pessoas).map(([codigo, pessoa]) => ({...pessoa, codigo})),
    });

    const chamaPrevia = (proximo: number) => {
        setErro(null);
        const dados = new FormData();
        dados.append('data', JSON.stringify(corpo()));
        setLoading(true);
        getApi().post('processo/abertura/previa', dados)
            .then(resposta => {
                setPrevia(resposta.data);
                setPasso(proximo);
            })
            .catch(e => setErro(erroDaApi(e, 'Não foi possível simular a abertura.')))
            .finally(() => setLoading(false));
    };

    const avancar = () => {
        setErro(null);
        if (passo === 0) {
            if (!fluxo || !cliente || !dataInicio) {
                setErro('Escolha o cliente, o fluxo e a data de início.');
                return;
            }
            if (repetidos.length && !confirmaRepetido) {
                setErro(`Confirme que quer abrir outro processo do fluxo ${fluxo.codigo} para este cliente.`);
                return;
            }
            setPasso(1);
        } else if (passo === 1) {
            chamaPrevia(2);
        } else if (passo === 2) {
            chamaPrevia(3);
        }
    };

    const abrir = () => {
        setErro(null);
        const dados = new FormData();
        dados.append('data', JSON.stringify(corpo()));
        setLoading(true);
        getApi().post('processo/add', dados)
            .then(resposta => {
                showAlert({msg: resposta.data.msg, type: 'success', toaster: true});
                history.replace(`/processos/${resposta.data.data.id}`);
            })
            .catch(e => setErro(erroDaApi(e, 'Não foi possível abrir o processo.')))
            .finally(() => setLoading(false));
    };

    const papeisUsados = useMemo(() => {
        if (!fluxo || !previa) {
            return [];
        }
        const usados = new Map(previa.papeis_usados.map(p => [p.codigo, p.itens]));
        return fluxo.papeis.filter(p => usados.has(p.codigo)).map(p => ({...p, itens: usados.get(p.codigo) || 0}));
    }, [fluxo, previa]);

    const mudaPessoa = (codigo: string, campo: keyof Pessoa, valor: any) => {
        setPessoas({...pessoas, [codigo]: {...pessoas[codigo], [campo]: valor}});
    };

    const dia = (data: string) => Math.round(new Date(`${data}T00:00:00Z`).getTime() / 86400000);
    const barra = (inicio: string, fim: string) => {
        if (!previa) return {};
        const de = dia(previa.inicio);
        const total = dia(previa.fim) - de + 1;
        return {left: `${(dia(inicio) - de) * 100 / total}%`, width: `${Math.max((dia(fim) - dia(inicio) + 1) * 100 / total, 1)}%`};
    };

    const totalItens = fluxo ? fluxo.itens_obrigatorios + fluxo.modulos_opcionais.filter(m => modulos.includes(m.codigo)).reduce((s, m) => s + m.itens, 0) : 0;

    return (
        <div className="pr-novo">
            <header className="pr-novo-topo">
                <button type="button" className="pr-voltar" onClick={() => history.push('/processos')} title="Voltar"><CIcon name="cil-arrow-left"/></button>
                <h4>Abrir processo</h4>
            </header>

            <ol className="pr-passos">
                {PASSOS.map((rotulo, i) => (
                    <li key={rotulo} className={i === passo ? 'atual' : i < passo ? 'feito' : ''}>
                        <span>{i < passo ? <CIcon name="cil-check"/> : i + 1}</span>
                        <em>{rotulo}</em>
                    </li>
                ))}
            </ol>

            {erro && <CAlert color="danger" className="pr-erro">{erro}</CAlert>}

            <div className="pr-novo-grade">
            <section className="pr-novo-corpo">
                {passo === 0 && (
                    <div className="pr-form colunas">
                        <label className="largo">
                            <span>Cliente *</span>
                            <AsyncSelect cacheOptions defaultOptions loadOptions={buscaClientes} value={cliente} placeholder="Digite o nome do cliente"
                                         noOptionsMessage={() => 'Nenhum cliente encontrado (inativos não aparecem)'}
                                         loadingMessage={() => 'Buscando…'}
                                         onChange={(valor: any) => setCliente(valor)} classNamePrefix="pr-select"/>
                        </label>
                        <label>
                            <span>Fluxo *</span>
                            <CSelect value={fluxoId} onChange={(e: any) => setFluxoId(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">Escolha…</option>
                                {fluxos.map(f => (
                                    <option key={f.id} value={f.id}>
                                        {f.codigo} · {f.nome} (v{f.versao}){fluxosDoCliente.has(f.id) ? ' · já tem processo' : ''}
                                    </option>
                                ))}
                            </CSelect>
                        </label>
                        {repetidos.length > 0 && (
                            <div className="pr-repetido" role="alert">
                                <strong>
                                    {cliente?.label} já tem {repetidos.length === 1 ? 'um processo' : `${repetidos.length} processos`} do fluxo {fluxo?.codigo}:
                                </strong>
                                <ul>
                                    {repetidos.map(p => (
                                        <li key={p.id}>
                                            <a href={`#/processos/${p.id}`} target="_blank" rel="noopener noreferrer">#{p.id} {p.titulo}</a>
                                            <span> · {p.situacao_nome} · início {dataBr(p.data_inicio)}</span>
                                        </li>
                                    ))}
                                </ul>
                                <label>
                                    <input type="checkbox" checked={confirmaRepetido} onChange={e => setConfirmaRepetido(e.target.checked)}/>
                                    Abrir outro processo deste fluxo para o cliente mesmo assim
                                </label>
                            </div>
                        )}
                        <div className="pr-form-linha">
                            <label>
                                <span>Início *</span>
                                <CInput type="date" value={dataInicio} onChange={(e: any) => setDataInicio(e.target.value)}/>
                            </label>
                            <label>
                                <span>Complexidade</span>
                                <CSelect value={complexidade} onChange={(e: any) => setComplexidade(e.target.value ? Number(e.target.value) : '')}>
                                    <option value="">—</option>
                                    <option value={1}>Baixa</option>
                                    <option value={2}>Média</option>
                                    <option value={3}>Alta</option>
                                </CSelect>
                            </label>
                        </div>
                        <label>
                            <span>Gestor (Gerente de Projetos)</span>
                            <CSelect value={gestorId} onChange={(e: any) => setGestorId(e.target.value ? Number(e.target.value) : '')}>
                                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                            </CSelect>
                        </label>
                        <label title="Canal que recebe quando o cliente responde, pergunta ou agenda pelo portal, e os atrasos do processo">
                            <span>Avisar no Mattermost</span>
                            <CSelect value={webhookId} onChange={(e: any) => setWebhookId(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">Não avisar no Mattermost</option>
                                {webhooks.map(w => <option key={w.id} value={w.id}>{w.nome}</option>)}
                            </CSelect>
                        </label>
                        <label className="largo">
                            <span>Título (opcional)</span>
                            <CInput value={titulo} maxLength={150} placeholder={fluxo && cliente ? `${fluxo.nome} — ${cliente.label}` : ''}
                                    onChange={(e: any) => setTitulo(e.target.value)}/>
                        </label>
                    </div>
                )}

                {passo === 1 && fluxo && (
                    <div className="pr-form">
                        <p className="pr-ajuda">
                            Todos os módulos vêm marcados: desmarque o que o cliente não contratou. Itens de módulos não marcados não entram no processo.
                            Já entram {fluxo.itens_obrigatorios} itens obrigatórios.
                        </p>
                        {fluxo.modulos_opcionais.length > 0 ? (
                            <label className="pr-modulos-todos">
                                <input type="checkbox" checked={modulos.length === fluxo.modulos_opcionais.length}
                                       ref={caixa => { if (caixa) caixa.indeterminate = modulos.length > 0 && modulos.length < fluxo.modulos_opcionais.length; }}
                                       onChange={e => setModulos(e.target.checked ? fluxo.modulos_opcionais.map(m => m.codigo) : [])}/>
                                Selecionar todos
                                <span>{modulos.length} de {fluxo.modulos_opcionais.length} módulos</span>
                            </label>
                        ) : (
                            <p className="pr-ajuda">Este fluxo não tem módulos opcionais.</p>
                        )}
                        <div className="pr-modulos">
                            {fluxo.modulos_opcionais.map(modulo => {
                                const marcado = modulos.includes(modulo.codigo);
                                return (
                                    <label key={modulo.codigo} className={`pr-modulo ${marcado ? 'marcado' : ''}`}>
                                        <input type="checkbox" checked={marcado}
                                               onChange={e => setModulos(e.target.checked ? [...modulos, modulo.codigo] : modulos.filter(m => m !== modulo.codigo))}/>
                                        <span className="pr-modulo-nome">{modulo.nome}</span>
                                        <span className="pr-modulo-itens">+{modulo.itens} itens</span>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="pr-total">Total: <strong>{totalItens}</strong> itens</div>
                    </div>
                )}

                {passo === 2 && (
                    <div className="pr-form">
                        <p className="pr-ajuda">
                            Quem responde por cada papel neste cliente. Papel sem pessoa fica como “sem responsável” e pode ser preenchido depois.
                        </p>
                        {pessoasPapel?.restrito && (
                            <p className="pr-ajuda pr-setores-fluxo">
                                <CIcon name="cil-people"/> {textoSetores(pessoasPapel)}
                                {pessoasPapel.usuarios.length === 0 && ' Nenhum usuário ativo neles: cadastre em Setores.'}
                            </p>
                        )}
                        <div className="pr-papeis-grade">
                        {papeisUsados.map(papel => {
                            const pessoa = pessoas[papel.codigo] || {};
                            return (
                                <div key={papel.codigo} className="pr-papel">
                                    <div className="pr-papel-nome">
                                        <strong>{papel.nome}</strong>
                                        <span>{papel.codigo} · {papel.itens} itens{papel.tipo === TIPO_PAPEL_CLIENTE ? ' · cliente' : ''}</span>
                                    </div>
                                    {papel.tipo === TIPO_PAPEL_CLIENTE ? (
                                        <div className="pr-papel-contato">
                                            <CInput placeholder="Nome do contato" value={pessoa.contato_nome || ''} onChange={(e: any) => mudaPessoa(papel.codigo, 'contato_nome', e.target.value)}/>
                                            <CInput type="email" placeholder="E-mail" value={pessoa.contato_email || ''} onChange={(e: any) => mudaPessoa(papel.codigo, 'contato_email', e.target.value)}/>
                                            <CInput placeholder="Telefone" value={pessoa.contato_telefone || ''} onChange={(e: any) => mudaPessoa(papel.codigo, 'contato_telefone', e.target.value)}/>
                                            <CInput placeholder="Cargo" value={pessoa.contato_cargo || ''} onChange={(e: any) => mudaPessoa(papel.codigo, 'contato_cargo', e.target.value)}/>
                                        </div>
                                    ) : (
                                        <CSelect value={pessoa.usuario_id ?? ''} onChange={(e: any) => mudaPessoa(papel.codigo, 'usuario_id', e.target.value ? Number(e.target.value) : '')}>
                                            <option value="">Sem responsável</option>
                                            {opcoesPapel.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                                        </CSelect>
                                    )}
                                </div>
                            );
                        })}
                        </div>
                    </div>
                )}

                {passo === 3 && previa && (
                    <div className="pr-form">
                        {repetidos.length > 0 && (
                            <div className="pr-repetido">
                                <strong>O cliente já tem processo deste fluxo ({repetidos.map(p => `#${p.id}`).join(', ')}). Você confirmou a abertura de outro.</strong>
                            </div>
                        )}
                        <div className="pr-resumo-abertura">
                            <div><span>Cliente</span><strong>{cliente?.label}</strong></div>
                            <div><span>Fluxo</span><strong>{fluxo?.codigo} · {fluxo?.nome}</strong></div>
                            <div><span>Itens</span><strong>{previa.itens}</strong></div>
                            <div className="pr-resumo-periodo">
                                <span>Início → término previsto</span>
                                <strong>{dataBr(previa.inicio)} → {dataBr(previa.fim)}</strong>
                                <em>{previa.dias_uteis} dias úteis</em>
                            </div>
                        </div>
                        <ol className="pr-previa-etapas">
                            {previa.etapas.map((etapa, i) => (
                                <li key={i}>
                                    <span className="pr-numero">{i + 1}</span>
                                    <span className="pr-previa-nome">{etapa.nome}<em>{etapa.itens} itens</em></span>
                                    <span className="pr-previa-trilho"><span style={barra(etapa.inicio, etapa.fim)}/></span>
                                    <span className="pr-previa-datas">{dataBr(etapa.inicio).slice(0, 5)} → {dataBr(etapa.fim).slice(0, 5)}</span>
                                </li>
                            ))}
                        </ol>
                        {previa.etapas_sem_itens.length > 0 && (
                            <p className="pr-ajuda">Sem itens com estes módulos, não entram: {previa.etapas_sem_itens.join(', ')}.</p>
                        )}
                    </div>
                )}
            </section>

            <aside className="pr-novo-resumo">
                <div className="pr-bloco-titulo">Resumo da abertura</div>
                <dl className="pr-datas">
                    <dt>Cliente</dt><dd>{cliente?.label || '—'}</dd>
                    <dt>Fluxo</dt><dd>{fluxo ? `${fluxo.codigo} · ${fluxo.nome}` : '—'}</dd>
                    <dt>Início</dt><dd>{dataBr(dataInicio) || '—'}</dd>
                    <dt>Gestor</dt><dd>{usuarios.find(u => u.id === gestorId)?.nome || '—'}</dd>
                    <dt>Mattermost</dt><dd>{webhooks.find(w => w.id === webhookId)?.nome || 'não avisa'}</dd>
                    <dt>Módulos</dt><dd>{fluxo ? (modulos.length ? fluxo.modulos_opcionais.filter(m => modulos.includes(m.codigo)).map(m => m.nome).join(', ') : 'só os obrigatórios') : '—'}</dd>
                    <dt>Itens</dt><dd>{fluxo ? totalItens : '—'}</dd>
                    <dt>Término</dt><dd>{previa ? `${dataBr(previa.fim)} · ${previa.dias_uteis} d.u.` : 'na prévia'}</dd>
                </dl>
            </aside>
            </div>

            <footer className="pr-novo-rodape">
                {passo > 0 && <CButton color="dark" variant="outline" onClick={() => setPasso(passo - 1)}>Voltar</CButton>}
                {passo < 3 && <CButton color="primary" onClick={avancar}>Continuar</CButton>}
                {passo === 3 && <CButton color="success" onClick={abrir}><CIcon name="cil-check"/> Abrir processo</CButton>}
            </footer>
        </div>
    );
};

export default ProcessoNovo;
